import React from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { Badge } from '../../visual-components/badge/badge';
import { AppHeader } from '../../visual-components/app-header/app-header';
import { useCartQuery } from '../cart/queries';
import { notNil } from '../../core/helpers/is-nil';
import { useLanguageChange } from '../../core/i18n/hooks/use-language-change';
import { useLanguageRequest } from '../user/mutations';
import { Language } from '../../core/i18n/constants';
import { AuthGuard } from '../auth/auth-guard';

type Props = {
  currentLanguage: Language;
  handleLogin: () => void;
  onSelectLocale: (locale: Language) => void;
};

const LoggedInAppHeader: React.FC<Props> = ({ currentLanguage, handleLogin, onSelectLocale: parentOnSelectLocale }) => {
  const { mutateAsync: sendLanguageChange } = useLanguageRequest();

  const { data: cart } = useCartQuery();
  const cartData = cart?.data;
  const hasCartData = notNil(cartData);

  const onSelectLocale = async (locale: Language) => {
    parentOnSelectLocale(locale);
    await sendLanguageChange({ language: locale });
  };

  return (
    <AppHeader
      isLoggedIn
      cartBadge={hasCartData && !cartData.isEmpty ? <Badge numberToShow={cartData.totalUniqueItems} /> : undefined}
      currentLanguage={currentLanguage}
      handleLogin={handleLogin}
      onSelectLocale={onSelectLocale}
    />
  );
};

const AnonymousAppHeader: React.FC<Props> = ({ currentLanguage, handleLogin, onSelectLocale }) => {
  return <AppHeader currentLanguage={currentLanguage} handleLogin={handleLogin} onSelectLocale={onSelectLocale} />;
};

export const AppHeaderManager: React.FC = () => {
  const { loginWithRedirect, isAuthenticated: isLoggedIn } = useAuth0();
  const { currentLanguage, changeLanguage } = useLanguageChange();

  const onSelectLocale = async (locale: Language) => {
    await changeLanguage(locale);
  };

  const handleLogin = () => {
    loginWithRedirect();
  };

  return isLoggedIn ? (
    <AuthGuard>
      <LoggedInAppHeader currentLanguage={currentLanguage} handleLogin={handleLogin} onSelectLocale={onSelectLocale} />
    </AuthGuard>
  ) : (
    <AnonymousAppHeader currentLanguage={currentLanguage} handleLogin={handleLogin} onSelectLocale={onSelectLocale} />
  );
};
